import React, { useMemo, useState } from "react";
import { Search, Check, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import VoiceMic from "@/components/VoiceMic";
import { copyLibraryImage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LIBRARY_CATEGORIES, LIBRARY_IMAGES, isLibraryImage, searchLibrary, suggestFor } from "@/lib/imageLibrary";

export const SampleBadge = ({ url, className = "", testid }) =>
  isLibraryImage(url) ? (
    <span className={`pointer-events-none inline-flex items-center rounded-full bg-black/60 text-white text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${className}`} title="Generic sample image from the VerB library — not an actual photo of this business" data-testid={testid}>Sample image</span>
  ) : null;

function Grid({ items, selected, onPick }) {
  if (!items.length) return <div className="text-center text-xs text-slate-500 py-10" data-testid="library-empty">No images match your search.</div>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="library-grid">
      {items.map((im) => (
        <button type="button" key={im.key} onClick={() => onPick(im)} className={`relative rounded-xl overflow-hidden border-2 transition-colors ${selected?.key === im.key ? "border-emerald-600" : "border-transparent hover:border-amber-400"}`} data-testid={`library-item-${im.key}`}>
          <img src={im.thumb} alt={im.title} loading="lazy" className="w-full aspect-square object-cover bg-slate-100" />
          <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[9px] sm:text-[10px] font-semibold px-1.5 pt-3 pb-1 text-left truncate">{im.title}</span>
          {selected?.key === im.key && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>}
        </button>
      ))}
    </div>
  );
}

function Suggestions({ items, selected, onPick }) {
  return (
    <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-2" data-testid="library-suggestions">
      <div className="text-[11px] font-semibold text-amber-900 mb-1.5">आपकी दुकान के लिए सुझाव / Suggested for your shop</div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {items.map((im) => (
          <button type="button" key={im.key} onClick={() => onPick(im)} className={`relative flex-shrink-0 w-20 rounded-lg overflow-hidden border-2 ${selected?.url === im.url ? "border-emerald-600" : "border-transparent hover:border-amber-400"}`} title={im.title} data-testid={`library-suggest-${im.key}`}>
            <img src={im.thumb} alt={im.title} loading="lazy" className="w-full aspect-square object-cover bg-slate-100" />
            {selected?.url === im.url && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center"><Check className="w-2.5 h-2.5" /></span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ImageLibraryDialog({ open, onOpenChange, initialCategory = "shops", onSelect, suggest }) {
  const [cat, setCat] = useState(initialCategory);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [voiceLang, setVoiceLang] = useState("hi-IN");
  const items = useMemo(() => {
    if (!q.trim()) return LIBRARY_IMAGES.filter((im) => im.category === cat);
    const seen = new Set();
    return searchLibrary(q).filter((im) => !seen.has(im.url) && seen.add(im.url));
  }, [cat, q]);
  const confirm = async () => {
    if (!sel || saving) return;
    setSaving(true);
    try {
      const r = await copyLibraryImage(sel.url);
      onSelect(r.url);
      onOpenChange(false); setSel(null);
    } catch {
      toast.error("Could not save this image to VerB storage. Please try again or upload your own photo.");
    } finally { setSaving(false); }
  };
  const picks = useMemo(() => suggestFor(suggest), [suggest]);
  const onVoice = (text) => { setQ(text.replace(/[।.!?,]+$/u, "").trim()); setSel(null); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] max-h-[90vh] overflow-y-auto p-3 sm:p-6" data-testid="image-library-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">VerB Image Library</DialogTitle>
          <DialogDescription className="text-xs">Ready-made professional images, free to use. They are marked as “Sample image” so customers know they are not your real photos — replace them with your own photo anytime.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: plumber, प्लंबर, mithai, दर्जी, tiffin…" className="w-full border border-slate-200 rounded-full pl-8 pr-9 py-2 text-sm outline-none focus:border-emerald-500" data-testid="library-search" />
            <VoiceMic onText={onVoice} lang={voiceLang} showUnsupported testid="library-voice" className="absolute right-1.5 top-1/2 -translate-y-1/2" />
          </div>
          <div className="flex rounded-full border border-slate-200 overflow-hidden flex-shrink-0 text-[10px] font-semibold" role="group" aria-label="Voice language" data-testid="library-voice-lang">
            {[["hi-IN", "हिंदी"], ["en-IN", "EN"]].map(([v, t]) => (
              <button type="button" key={v} onClick={() => setVoiceLang(v)} aria-pressed={voiceLang === v} className={`px-2 py-1 ${voiceLang === v ? "bg-emerald-700 text-white" : "bg-white text-slate-600"}`} data-testid={`library-voice-lang-${v.slice(0, 2)}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="library-categories">
          {LIBRARY_CATEGORIES.map((c) => (
            <button type="button" key={c.id} onClick={() => { setCat(c.id); setQ(""); }} className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold border ${!q && cat === c.id ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-amber-400"}`} data-testid={`library-cat-${c.id}`}>{c.label}</button>
          ))}
        </div>
        {!q.trim() && picks.length > 0 && <Suggestions items={picks} selected={sel} onPick={setSel} />}
        <Grid items={items} selected={sel} onPick={setSel} />
        {sel && (
          <div className="sticky bottom-0 -mx-3 sm:-mx-6 -mb-3 sm:-mb-6 mt-2 bg-white border-t border-slate-100 p-3 flex items-center gap-3" data-testid="library-preview">
            <img src={sel.thumb} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{sel.title}</div>
              <div className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Info className="w-3 h-3" /> Will be labelled “Sample image”</div>
            </div>
            <button type="button" onClick={confirm} disabled={saving} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs font-semibold flex-shrink-0 disabled:opacity-60" data-testid="library-use-btn">{saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : "Use this image"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
